import React, { useState, useEffect } from 'react';
import { Box, Switch, Slider, Typography, Paper, Button, Stack } from '@mui/material';
import {
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import useApp from '../../store/contexts/AppContext';

export const POPUP_NOTIF_PREF_KEY = 'popupNotifPref';
export const loadPopupNotifPref = () => {
  try { return localStorage.getItem(POPUP_NOTIF_PREF_KEY) || 'global'; } catch { return 'global'; }
};
const savePopupNotifPref = (val) => {
  try { localStorage.setItem(POPUP_NOTIF_PREF_KEY, val); } catch {}
};

const SettingsContainer = styled(Paper)(({ theme }) => ({
  padding: '20px',
  borderRadius: '12px',
  background: theme.palette.mode === 'dark'
    ? alpha('#1a1f2e', 0.95)
    : alpha('#ffffff', 0.95),
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
  boxShadow: theme.palette.mode === 'dark'
    ? `0 4px 12px ${alpha('#000', 0.3)}`
    : `0 4px 12px ${alpha('#000', 0.1)}`,
}));

const SettingItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 0',
  gap: '16px',
});

const SettingLabel = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flex: 1,
});

const SettingTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: theme.palette.mode === 'dark' ? '#fff' : '#000',
}));

const SettingDescription = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.mode === 'dark' ? alpha('#fff', 0.6) : alpha('#000', 0.6),
  marginTop: '2px',
}));

const VolumeSliderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  width: '100%',
  marginTop: '8px',
  padding: '8px 0',
}));

const popupOptions = [
  { value: 'global',   label: 'Everywhere',    desc: 'Show on all pages' },
  { value: 'messages', label: 'Messages only',  desc: 'Show only on the messages page' },
  { value: 'off',      label: 'Off',            desc: 'Never show popup notifications' },
];

const NotificationSettings = () => {
  const { notificationSound } = useApp();
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [volume, setVolume] = useState(0.5);
  const [popupPref, setPopupPref] = useState(loadPopupNotifPref);

  useEffect(() => {
    if (notificationSound) {
      setSoundEnabled(notificationSound.isEnabled());
      setVolume(notificationSound.getVolume());
    }
  }, [notificationSound]);

  const handlePopupPref = (val) => {
    setPopupPref(val);
    savePopupNotifPref(val);
  };

  const handleSoundToggle = () => {
    if (notificationSound) {
      const newState = notificationSound.toggle();
      setSoundEnabled(newState);

      // Play a test sound if enabled
      if (newState) {
        setTimeout(() => {
          notificationSound.play('success');
        }, 100);
      }
    }
  };

  const handleVolumeChange = (event, newValue) => {
    const newVolume = newValue / 100;
    setVolume(newVolume);
    if (notificationSound) {
      notificationSound.setVolume(newVolume);
    }
  };

  const handleVolumeChangeCommitted = () => {
    // Play test sound when user releases slider
    if (notificationSound && soundEnabled) {
      notificationSound.play('message');
    }
  };

  return (
    <SettingsContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <NotificationsIcon sx={{ fontSize: 20, opacity: 0.7 }} />
        <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 700 }}>
          Notification Settings
        </Typography>
      </Box>

      <SettingItem>
        <SettingLabel>
          <Box>
            {soundEnabled ? (
              <VolumeUpIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            ) : (
              <VolumeOffIcon sx={{ fontSize: 24, opacity: 0.5 }} />
            )}
          </Box>
          <Box>
            <SettingTitle>Notification Sounds</SettingTitle>
            <SettingDescription>
              Play a sound when you receive new messages
            </SettingDescription>
          </Box>
        </SettingLabel>
        <Switch
          checked={soundEnabled}
          onChange={handleSoundToggle}
          color="primary"
        />
      </SettingItem>

      {soundEnabled && (
        <VolumeSliderContainer>
          <VolumeOffIcon sx={{ fontSize: 20, opacity: 0.5 }} />
          <Slider
            value={volume * 100}
            onChange={handleVolumeChange}
            onChangeCommitted={handleVolumeChangeCommitted}
            min={0}
            max={100}
            sx={{
              flex: 1,
              '& .MuiSlider-thumb': {
                width: 16,
                height: 16,
              },
            }}
          />
          <VolumeUpIcon sx={{ fontSize: 20, opacity: 0.7 }} />
          <Typography
            variant="caption"
            sx={{ minWidth: '40px', textAlign: 'right', opacity: 0.7 }}
          >
            {Math.round(volume * 100)}%
          </Typography>
        </VolumeSliderContainer>
      )}

      <Box sx={{ mt: 2, pt: 2, borderTop: (t) => `1px solid ${alpha(t.palette.divider, 0.4)}` }}>
        <SettingLabel sx={{ mb: 1.5 }}>
          <NotificationsIcon sx={{ fontSize: 22, color: 'primary.main' }} />
          <Box>
            <SettingTitle>Popup Notifications</SettingTitle>
            <SettingDescription>Choose where message popups appear</SettingDescription>
          </Box>
        </SettingLabel>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {popupOptions.map((opt) => (
            <Button
              key={opt.value}
              variant={popupPref === opt.value ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handlePopupPref(opt.value)}
              sx={{ textTransform: 'none', flex: 1, flexDirection: 'column', py: 1 }}
            >
              <Typography variant="body2" fontWeight={600}>{opt.label}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.75, display: { xs: 'none', sm: 'block' } }}>
                {opt.desc}
              </Typography>
            </Button>
          ))}
        </Stack>
      </Box>
    </SettingsContainer>
  );
};

export default NotificationSettings;
